import store from '@/store'
import { ROLES } from '@/config/permissions'

/**
 * Vérifie si l'utilisateur possède un des rôles demandés.
 *
 * @param {string[]} value - Rôles à vérifier
 * @param {Object} [options]
 * @param {boolean} [options.bypassSuperAdmin=true] - Si true, super admin retourne toujours true
 *   (sauf si on check explicitement le rôle extranet client)
 * @returns {boolean}
 *
 * @example
 *   $checkRoles([ROLES.SUPER_ADMIN, ROLES.COMMERCIAL])
 */
export const checkRoles = (value, { bypassSuperAdmin = true } = {}) => {
  if (!Array.isArray(value) || value.length === 0) {
    console.warn('checkRoles: expected non-empty array of roles')
    return false
  }

  const roles = store.getters?.roles || []

  // Super admin bypass (sauf si on check explicitement le rôle extranet client)
  if (bypassSuperAdmin && !value.includes(ROLES.EXTRANET_CLIENT)) {
    if (roles.includes(ROLES.SUPER_ADMIN)) return true
  }

  return value.some(r => roles.includes(r))
}

/**
 * L'utilisateur connecté relève-t-il du portail client ?
 *
 * À utiliser partout où le code cherchait le rôle « extranet client » dans la
 * liste des rôles. Ce nom n'est plus qu'un profil extranet parmi d'autres
 * (`extranet consultation`, `facturation`, `exploitation`, `administrateur`) :
 * le chercher nommément rangeait un client légitime du côté interne, et lui
 * servait le mauvais layout. Le côté est calculé par le serveur
 * (`UserResource.scope`) et le super admin ne le contourne pas.
 */
export const isExtranetUser = () => store.getters?.isExtranet === true

/**
 * Un utilisateur DONNÉ (fiche consultée, ligne de liste) relève-t-il du portail
 * client ? À ne pas confondre avec `isExtranetUser()`, qui parle de
 * l'utilisateur connecté.
 *
 * Le serveur renseigne `scope` (UserResource) ; le repli sur le préfixe du rôle
 * couvre les réponses d'un backend antérieur et reste juste pour les quatre
 * profils, tous nommés « extranet … ».
 */
export const isExtranetProfile = (user) => {
  if (user?.scope) return user.scope === 'extranet'
  return (user?.roles || []).some(r => typeof r === 'string' && r.startsWith('extranet '))
}
